import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Activity, Building2, FileText, LineChart, Shield, Sprout } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { getFavoritesCache, setFavoritesCache } from '../lib/cache';

interface FavoriteFunction {
  id: string;
  name: string;
  slug: string;
  group: string;
}

const iconComponents = {
  LineChart,
  Building2,
  FileText,
  Shield,
  Sprout,
  Activity,
};

const groupIconMap: Record<string, keyof typeof iconComponents> = {
  'market': 'LineChart',
  'leasing': 'FileText',
  'finance': 'Building2',
  'property': 'Building2',
  'risk': 'Shield',
  'specialty': 'Sprout',
};

const DEFAULT_ICON = 'Activity';

export function FavoriteFeatures() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<FavoriteFunction[]>([]);
  const [loading, setLoading] = useState(true);
  const { refreshTrigger } = useFavorites();

  useEffect(() => {
    if (!user) return;

    const fetchFavorites = async () => {
      try {
        // Check cache first
        const cachedData = getFavoritesCache(user.id);
        if (cachedData) {
          setFavorites(cachedData.favorites);
          setLoading(false);

          // Fetch in background to update cache
          fetchFromDatabase();
          return;
        }

        await fetchFromDatabase();
      } catch (err) {
        console.error('Error fetching favorites:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchFromDatabase = async () => {
      try {
        const { data, error } = await supabase
          .from('user_favorites')
          .select(`
            feature:features (
              id,
              name,
              slug,
              group
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const favoritesData = data
          .map(item => item.feature)
          .filter((f): f is FavoriteFunction => f !== null);

        setFavorites(favoritesData);
        setFavoritesCache(user.id, { 
          favorites: favoritesData,
          timestamp: Date.now()
        });
      } catch (error) {
        console.error('Error fetching from database:', error);
        throw error;
      }
    };

    fetchFavorites();
  }, [user, refreshTrigger]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-12 bg-[--bg-tertiary] rounded animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (!favorites.length) {
    return (
      <div className="text-center py-4 bg-[--bg-secondary] rounded-lg">
        <Star className="mx-auto h-12 w-12 text-[--text-disabled]" />
        <h3 className="mt-2 text-sm font-medium text-[--text-primary]">No favorites yet</h3>
        <p className="mt-1 text-sm text-[--text-secondary]">
          Add features to your favorites for quick access
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      {favorites.map((func) => {
        const mainGroup = func.group.split('-')[0].toLowerCase();
        const IconComponent = iconComponents[groupIconMap[mainGroup] || DEFAULT_ICON];
        
        return (
          <div key={func.id} className="relative group">
            <button
              onClick={() => navigate(`/${func.slug}`)}
              className="w-full bg-[--bg-primary] shadow-sm hover:shadow-md rounded-lg py-2 px-3 transition-shadow duration-200"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <IconComponent className="h-5 w-5 text-primary-500" />
                </div>
                <div className="ml-3 flex-1 text-left truncate">
                  <h3 className="text-sm font-medium text-[--text-primary] truncate">
                    {func.name}
                  </h3>
                </div>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}